 
import React, { useEffect, useState } from 'react';
import {
  CountFrequency,
  getEmptyOtherClaimsModalState,
  OtherClaimsModalType,
} from '../../../pages/FinancialForensicsPage/FinancialForensics.types';
import { Grid, Modal, Paper } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { scrollBarStyles } from '../../../core-utils/messages/CommonConstants';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import {
  Button,
  Delete,
  EditableTable,
  FileDropZone,
  FilePresent,
  OrDivider,
  PageTitleAndSubTitle,
  RadioButton,
  TextField,
  UploadedDocument,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { financialForensicsConstants } from '../../../core-utils/messages/financialForensicsConstants';
import SvgClose from '@HomaHealth/ui-component-library/lib/_components/atoms/Icons/Close';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { removeEmptyFields } from '../../../core-utils/helper-utils/riskRegisterHelper';
import { formatFileSize } from '../../../core-utils/helper-utils/TaskBoardHelper';
import ReportCreateNewLabelField from '../../atoms/ReportCreateNewLabelField';
import { editableColumn } from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/EditableTable';
import { generateDateRanges } from '../../../core-utils/helper-utils/financialForensicsHelper';
import {
  Detail,
  emptyVendorEntry,
  VendorEntry,
} from '../../../pages/FinancialForensicsPage/types/VariableCost.types';
import { SelectDetails } from '../AddReimbursementModal';

export interface AddOtherClaimLineModalProps {
  open: OtherClaimsModalType;
  setOpen: React.Dispatch<React.SetStateAction<OtherClaimsModalType>>;
  onClose: () => void;
   
  handleSubmit: (data: any, vendorID?: string) => void;
  startDate: string;
  endDate: string;
   
  handleDelete: (vendorId: string) => void;
  dataFromProps?: VendorEntry;
}

const INITIAL_DETAIL_OPTIONS: SelectDetails[] = [
  { name: 'Monthly', checked: false },
  { name: 'Quarterly', checked: false },
  { name: 'Annually', checked: false },
];

const AddOtherClaimLineModal: React.FC<AddOtherClaimLineModalProps> = ({
  open,
  setOpen,
  onClose,
  handleSubmit,
  startDate,
  endDate,
  handleDelete,
  dataFromProps,
}) => {
  const useStyles = makeStyles()({
    modal: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    paper: {
      width: '1300px',
      height: '600px',
      padding: '24px 28px',
      backgroundColor: patientAccessTheme.palette.status.white,
      border: 'none !important',
      position: 'relative',

      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
    },
    titleContainer: {
      display: 'flex',
    },
    buttonContainer: {
      display: 'flex',
      justifyContent: 'flex-end',
    },
    button: { padding: '0px' },
    scroll: scrollBarStyles,
    flex: {
      display: 'flex',
      gap: '2rem',
    },
    service: {
      backgroundColor: patientAccessTheme.palette.grey[100],
      padding: '1rem 2rem',
      height: 'fit-content',
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      gap: '15px',
      overflowY: 'scroll',
      height: '440px',
      paddingRight: '1rem',
    },
  });
  const { classes } = useStyles();

  const [detailOptions, setDetailOptions] = useState<SelectDetails[]>(
    INITIAL_DETAIL_OPTIONS,
  );
  const [data, setData] = useState<VendorEntry>(emptyVendorEntry());
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  const isCommonFieldsFilled =
    data.claims &&
    data.feeCalculation.details &&
    Array.isArray(data.feeCalculation.details) &&
    data.feeCalculation.details.length > 0 &&
    data.feeCalculation.details?.every(
      (item: any) => item.type && item.values.length > 0,
    );

  const clearData = () => {
    setData(emptyVendorEntry());
    setDetailOptions(INITIAL_DETAIL_OPTIONS);
    setUploadedFiles([]);
  };

  const handleClose = () => {
    clearData();
    onClose();
    setOpen(getEmptyOtherClaimsModalState());
  };

  const handleModalSubmit = () => {
    const cleanedData = removeEmptyFields(data);
    handleSubmit(
      { ...cleanedData, files: uploadedFiles },
      open.selectedVendorID,
    );
    handleClose();
  };

  const handleDetailsOptionChange = (index: number) => {
    const updatedOptions = detailOptions.map((opt, i) => ({
      ...opt,
      checked: i === index,
    }));
    setDetailOptions(updatedOptions);

    const selected = updatedOptions[index];
    if (!selected.checked) {
      return;
    }

    const headers = generateDateRanges(selected.name, startDate, endDate);
    const values = headers.map((dateRange) => ({ dateRange, value: null }));

    setData((prev) => {
      const existingDetails: Detail[] =
        prev.feeCalculation.details.length > 0
          ? prev.feeCalculation.details
          : [{ type: prev.claims || 'New Claims', values: [] }];

      return {
        ...prev,
        feeCalculation: {
          ...prev.feeCalculation,
          detailsType: selected.name as CountFrequency,
          details: existingDetails.map((detail) => ({ ...detail, values })),
        },
      };
    });
  };

  const getColumnsForTable = (): editableColumn[] => {
    const firstDetail = data.feeCalculation.details[0];
    if (!firstDetail?.values?.length) {
      return [];
    }

    const firstColumn: editableColumn = {
      key: 'type',
      label: 'Type',
      editable: false,
      width: '150px',
    };

    const dateRangeColumns: editableColumn[] = firstDetail.values.map(
      (entry) => ({
        key: entry.dateRange,
        label: entry.dateRange,
        editable: true,
        type: 'number',
      }),
    );

    return [firstColumn, ...dateRangeColumns];
  };

  const getTableData = () =>
    data.feeCalculation.details.map((detail) => {
      const row: Record<string, string | number | null> = { type: detail.type };
      detail.values.forEach((entry) => {
        row[entry.dateRange] = entry.value;
      });
      return row;
    });

  const handleTableDataChange = (updatedRows: any[]) => {
    const updatedDetails: Detail[] = updatedRows.map((row, index) => {
      const { type, ...rest } = row;
      return {
        type: type ?? data.feeCalculation.details[index]?.type ?? '',
        values: Object.entries(rest).map(([dateRange, value]) => ({
          dateRange,
          value: value as number | null,
        })),
      };
    });

    setData((prev) => ({
      ...prev,
      feeCalculation: { ...prev.feeCalculation, details: updatedDetails },
    }));
  };

  useEffect(() => {
    if (!dataFromProps?.feeCalculation?.detailsType) {
      return;
    }

    const updatedDetailsOpt = detailOptions.map((opt) => ({
      ...opt,
      checked: opt.name === dataFromProps.feeCalculation.detailsType,
    }));
    setDetailOptions(updatedDetailsOpt);

    const headers = generateDateRanges(
      dataFromProps.feeCalculation.detailsType,
      startDate,
      endDate,
    );

    const normalizedDetails: Detail[] = dataFromProps.feeCalculation.details.map(
      (detail) => ({
        type: detail.type,
        values: headers.map((dateRange) => ({
          dateRange,
          value:
            detail.values.find((v) => v.dateRange === dateRange)?.value ?? null,
        })),
      }),
    );

    setData({
      ...dataFromProps,
      feeCalculation: {
        ...dataFromProps.feeCalculation,
        details: normalizedDetails,
      },
    });
  }, [dataFromProps]);

  return (
    <Modal open={open.open} onClose={handleClose} className={classes.modal}>
      <Paper className={classes.paper}>
        <Grid className={classes.titleContainer}>
          <PageTitleAndSubTitle
            title={financialForensicsConstants.addClaimLine}
            subTitle={''}
            subTitleStyles={{ width: '0px' }}
          />
          <SvgClose
            style={{ cursor: 'pointer' }}
            color={patientAccessTheme.palette.status.icon}
            onClick={handleClose}
          />
        </Grid>

        <Grid className={`${classes.scroll} ${classes.content}`}>
          <Grid className={`${classes.flex} ${classes.service}`}>
            <ReportCreateNewLabelField
              label={financialForensicsConstants.name}
              component={
                <TextField
                  width="535px"
                  height="36px"
                  placeholder={financialForensicsConstants.name}
                  onChange={(event: any) =>
                    setData((prev) => ({ ...prev, claims: event.target.value }))
                  }
                  value={data.claims}
                  placeholderStyle={{ fontSize: '16px' }}
                  error={!data.claims}
                />
              }
            />

            <ReportCreateNewLabelField
              label={financialForensicsConstants.detailsQuestion}
              component={
                <div
                  style={{ display: 'flex', gap: '20px', marginBottom: '10px' }}
                >
                  {detailOptions.map((option, index) => (
                    <label
                      key={option.name}
                      style={{ display: 'flex', alignItems: 'center' }}
                    >
                      <RadioButton
                        color={patientAccessTheme.palette.primary[400]}
                        checked={option.checked}
                        handleChange={() => handleDetailsOptionChange(index)}
                      />
                      <span style={{ marginLeft: '6px' }}>
                        <Typography
                          textColor={
                            patientAccessTheme.palette.text.highemphasis
                          }
                          fontSize="12px"
                          lineHeight="12px"
                          fontType="semiBold"
                        >
                          {option.name}
                        </Typography>
                      </span>
                    </label>
                  ))}
                </div>
              }
            />
          </Grid>

          {data.feeCalculation.detailsType && (
            <Grid>
              <EditableTable
                data={getTableData()}
                columns={getColumnsForTable()}
                onChange={handleTableDataChange}
                containerStyles={{
                  overflow: 'visible',
                  height: 'auto',
                  overflowY: 'visible',
                  width: '100%',
                  '& table': { width: '100% !important' },
                }}
                addIconColumnIndex={0}
                bodyCellStyles={{
                  padding: '0.5rem',
                  '& input[type=number]': { MozAppearance: 'textfield' },
                  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                }}
              />
            </Grid>
          )}

          <OrDivider containerStyles={{ margin: 0 }} />

          <Typography
            textColor={patientAccessTheme.palette.text.highemphasis}
            fontSize="12px"
            lineHeight="12px"
            fontType="medium"
          >
            {financialForensicsConstants.upload}
          </Typography>
          <FileDropZone
            filesSupported={['.pdf', '.doc', '.docx']}
            uploadedFiles={uploadedFiles}
            setUploadedFiles={setUploadedFiles}
            isMultiple={true}
          />
          {uploadedFiles.map((file, index) => (
            <UploadedDocument
              key={index}
              width="592px"
              name={file.name}
              truncateWidth="420px"
              size={formatFileSize(file.size)}
              startIcon={
                <FilePresent
                  stroke={patientAccessTheme.palette.primary[500]}
                  fill={patientAccessTheme.palette.primary[500]}
                />
              }
              handleDelete={() =>
                setUploadedFiles((prev) => prev.filter((_, i) => i !== index))
              }
            />
          ))}
        </Grid>

        <Grid className={classes.buttonContainer}>
          <Button
            label={
              <>
                <Typography
                  textColor={patientAccessTheme.palette.status.error}
                  fontSize="12px"
                  lineHeight="12px"
                  fontType="medium"
                >
                  {financialForensicsConstants.deleteClaim}
                </Typography>
                <Delete stroke={patientAccessTheme.palette.status.error} />
              </>
            }
            variant="text"
            height="36px"
            width="180px"
            styleProps={{ marginRight: '25px' }}
            borderRadius="4px"
            className={classes.button}
            onClick={() => {
              handleDelete(data.vendorID);
              clearData();
              setOpen(getEmptyOtherClaimsModalState());
            }}
          />
          <Button
            label={
              <Typography
                textColor={patientAccessTheme.palette.primary.main}
                fontSize="12px"
                lineHeight="12px"
                fontType="medium"
              >
                {financialForensicsConstants.Save_update}
              </Typography>
            }
            variant="outlined"
            height="36px"
            width="180px"
            styleProps={{ marginRight: '25px' }}
            borderRadius="4px"
            className={classes.button}
            onClick={handleModalSubmit}
          />
          <Button
            label={
              <Typography
                textColor={patientAccessTheme.palette.status.white}
                fontSize="12px"
                lineHeight="12px"
                fontType="medium"
              >
                {financialForensicsConstants.submit}
              </Typography>
            }
            variant="contained"
            color="primary"
            height="36px"
            width="180px"
            borderRadius="4px"
            className={classes.button}
            onClick={handleModalSubmit}
            disabled={!isCommonFieldsFilled}
          />
        </Grid>
      </Paper>
    </Modal>
  );
};

export default AddOtherClaimLineModal;
